import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageSquareWarning, Check } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Button, Field, Input, Modal, Select, Textarea, cn } from './ui';
import type { FeedbackKind } from '@shared/types';

/**
 * "Something is wrong" / "I wish it did this", from anywhere in the app.
 *
 * ── Why it sits next to Sign out ──────────────────────────────────────────
 *
 * Because that is the one control in the same place on every screen, and
 * because the moment somebody is most likely to report a problem is the moment
 * they have given up on the screen they are looking at. A support address in a
 * footer collects reports only from people determined enough to leave the app
 * and compose an email, which is almost nobody — and never the tenant who hit
 * a broken page and simply closed it.
 *
 * The current path and browser are attached automatically. They are the two
 * facts that turn "it doesn't work" into something fixable, and the two a
 * person writing in never thinks to include.
 */

const KINDS: { value: FeedbackKind; label: string; prompt: string }[] = [
  {
    value: 'bug',
    label: 'Something is broken',
    prompt: 'What were you doing, and what happened instead of what you expected?',
  },
  {
    value: 'idea',
    label: 'I have a suggestion',
    prompt: 'What would you like it to do? It helps to say what you are trying to get done.',
  },
  {
    value: 'question',
    label: 'I have a question',
    prompt: 'What are you trying to work out?',
  },
  { value: 'other', label: 'Something else', prompt: 'Tell us whatever it is.' },
];

export function FeedbackButton({ className, label = 'Report a problem' }: {
  className?: string;
  label?: string;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={cn(
          'flex items-center gap-3 w-full min-w-0 px-3 py-2 rounded-xl',
          'text-sm font-medium text-muted transition-colors hover:text-ink hover:bg-raised',
          className,
        )}
      >
        <MessageSquareWarning className="h-[18px] w-[18px] shrink-0" />
        <span className="truncate">{label}</span>
      </button>
      <FeedbackDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}

/** The same thing as an icon, for the portal's tight header row. */
export function FeedbackIconButton({ className }: { className?: string }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label="Report a problem or suggest something"
        title="Report a problem or suggest something"
        className={cn(
          'p-2 rounded-lg text-faint hover:text-ink hover:bg-raised transition-colors',
          className,
        )}
      >
        <MessageSquareWarning className="h-4 w-4" />
      </button>
      <FeedbackDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function FeedbackDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const location = useLocation();
  const [kind, setKind] = useState<FeedbackKind>('bug');
  const [body, setBody] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const chosen = KINDS.find((k) => k.value === kind)!;

  const close = () => {
    onClose();
    // Reset only after the dialog has gone, so the form does not visibly
    // empty itself on the way out.
    setTimeout(() => { setSent(false); setBody(''); setError(null); }, 200);
  };

  const submit = async () => {
    if (body.trim().length < 5) {
      setError('Tell us a little more — a few words is not enough to act on.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.post('/feedback', {
        kind,
        body: body.trim(),
        reply_to: replyTo.trim() || undefined,
        page_path: location.pathname,
        // Trimmed hard: it is a diagnostic, not a document.
        user_agent: navigator.userAgent.slice(0, 300),
      });
      setSent(true);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'That could not be sent. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (sent) {
    return (
      <Modal
        open={open}
        onClose={close}
        title="Thank you"
        footer={<Button variant="primary" onClick={close}>Close</Button>}
      >
        <div className="flex gap-3">
          <div className="shrink-0 h-9 w-9 rounded-full bg-ok/12 flex items-center justify-center">
            <Check className="h-5 w-5 text-ok" />
          </div>
          <div className="space-y-2 text-sm">
            <p>That has gone straight to the people who build PROVIA.</p>
            <p className="text-muted">
              You can see what you have sent, and what came of it, next time you
              open this form. If we need to ask you something we will use the
              address on your account.
            </p>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title="Tell us what is wrong"
      subtitle="It goes to the people who build PROVIA, not to your landlord or your letting agent."
      footer={(
        <>
          <Button onClick={close}>Cancel</Button>
          <Button variant="primary" onClick={submit} loading={saving}>Send</Button>
        </>
      )}
    >
      <div className="space-y-4">
        <Field label="What kind of thing is it?">
          <Select value={kind} onChange={(e) => setKind(e.target.value as FeedbackKind)}>
            {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
          </Select>
        </Field>

        <Field label="In your own words" hint={chosen.prompt} required>
          <Textarea
            rows={5}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={chosen.prompt}
            autoFocus
          />
        </Field>

        <Field
          label="Where should we reply?"
          hint="Leave it blank to use the address on your account."
        >
          <Input
            type="email"
            value={replyTo}
            onChange={(e) => setReplyTo(e.target.value)}
            placeholder="you@example.com"
          />
        </Field>

        <p className="text-xs text-faint">
          We attach the screen you are on ({location.pathname}) and which browser
          you are using. Nothing else about your data is sent.
        </p>

        {error && <p className="text-sm text-danger">{error}</p>}
      </div>
    </Modal>
  );
}
